import { useState, useEffect, useRef } from 'react'
import flowIcon from '@assets/image_1777447102475.png'
import { track } from '../analytics.js'

const SERVICES = [
  { name: 'Netflix',    color: '#e50914' },
  { name: 'Prime',      color: '#00a8e0' },
  { name: 'Disney+',    color: '#113ccf' },
  { name: 'Max',        color: '#b12fff' },
  { name: 'Hulu',       color: '#3dba79' },
  { name: 'Apple TV+',  color: '#888888' },
  { name: 'Peacock',    color: '#e8892b' },
  { name: 'Paramount+', color: '#0064ff' },
]

const POSITIONS = [
  { left: '6%',  top: '13%', rotate: -13 },
  { left: '62%', top: '8%',  rotate:   8 },
  { left: '4%',  top: '57%', rotate:  -6 },
  { left: '66%', top: '65%', rotate:  11 },
  { left: '52%', top: '17%', rotate:  -4 },
  { left: '16%', top: '78%', rotate:   7 },
  { left: '70%', top: '38%', rotate:  -9 },
  { left: '36%', top: '83%', rotate:   5 },
]

const KEYFRAMES = `
  @keyframes sbFadeUp {
    from { opacity:0; transform:translateY(22px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes sbBadgeIn {
    0%   { opacity:0; transform:scale(0.25); filter:blur(8px); }
    65%  { opacity:1; transform:scale(1.08); filter:blur(0); }
    100% { opacity:1; transform:scale(1); }
  }
  @keyframes sbBadgeJitter {
    0%,100% { transform:translateY(0) rotate(0deg); }
    25%     { transform:translateY(-3px) rotate(1.2deg); }
    75%     { transform:translateY(2px) rotate(-1deg); }
  }
  @keyframes sbIconRise {
    from { opacity:0; transform:scale(0.5) translateY(30px); filter:blur(14px); }
    to   { opacity:1; transform:scale(1) translateY(0); filter:blur(0); }
  }
  @keyframes sbGlowPulse {
    0%,100% { opacity:0.55; transform:scale(1); }
    50%     { opacity:0.95; transform:scale(1.12); }
  }
  @keyframes sbFloat {
    0%,100% { transform:translateY(0px); }
    50%     { transform:translateY(-9px); }
  }
`

const EXIT_MS = 480
const AUTO_DISMISS_MS = 5200

// Responsive helpers — mobile-first values scale up to desktop
const sz = {
  onceText:    'clamp(20px, 2.4vw, 34px)',
  simpleText:  'clamp(28px, 4vw,   56px)',
  frustText:   'clamp(13px, 1.3vw, 17px)',
  badgeText:   'clamp(10px, 0.9vw, 13px)',
  badgePad:    'clamp(4px, 0.45vw, 7px) clamp(9px, 0.9vw, 13px)',
  icon:        'clamp(88px, 9vw,  128px)',
  iconRadius:  'clamp(20px, 2.1vw, 28px)',
  flowTitle:   'clamp(46px, 5.5vw, 76px)',
  tagline:     'clamp(13px, 1.3vw, 18px)',
  sub:         'clamp(11px, 1.05vw, 14px)',
  ctaText:     'clamp(14px, 1.15vw, 16px)',
  ctaPad:      'clamp(13px, 1.2vw, 17px) clamp(34px, 3.5vw, 48px)',
  glow:        'clamp(420px, 45vw, 620px)',
  iconGlow:    'clamp(180px, 20vw, 280px)',
  skipText:    'clamp(9px, 0.85vw, 11px)',
}

export default function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState(0)
  const [exiting, setExiting] = useState(false)
  const skippedRef = useRef(false)

  const exit = (isSkip = false) => {
    if (exiting) return
    if (isSkip && !skippedRef.current) {
      skippedRef.current = true
      track('splash_skipped', { at_phase: phase })
    }
    setExiting(true)
    setTimeout(onDone, EXIT_MS)
  }

  useEffect(() => {
    // Phases: 1=once-upon, 2=streaming-simple, 3=badge-chaos, 4=frustration-copy, 5=flow-hero, 6=tagline+cta
    const schedule = [300, 900, 1700, 2500, 3400, 4200]
    const timers = schedule.map((ms, i) => setTimeout(() => setPhase(i + 1), ms))
    const auto = setTimeout(() => exit(false), AUTO_DISMISS_MS)
    return () => { ;[...timers, auto].forEach(clearTimeout) }
  }, [])

  const show = (n) => phase >= n

  return (
    <div
      onClick={() => exit(true)}
      style={{
        position: 'fixed', inset: 0, zIndex: 600,
        background: 'radial-gradient(ellipse at 50% 65%, #030d1a 0%, #010508 100%)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', cursor: 'pointer',
        fontFamily: "'Sora','SF Pro Display','Helvetica Neue',sans-serif",
        opacity: exiting ? 0 : 1,
        transform: exiting ? 'scale(1.05)' : 'scale(1)',
        transition: exiting ? `opacity ${EXIT_MS}ms ease, transform ${EXIT_MS}ms ease` : 'none',
        userSelect: 'none',
        pointerEvents: exiting ? 'none' : 'all',
      }}
    >
      <style>{KEYFRAMES}</style>

      {/* Deep ambient glow */}
      <div style={{
        position: 'absolute',
        width: sz.glow, height: sz.glow,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(30,100,210,0.14) 0%, transparent 65%)',
        animation: 'sbGlowPulse 5s ease-in-out infinite',
        pointerEvents: 'none',
      }} />

      {/* ── PHASE 1: "Once Upon a Time," ── */}
      {show(1) && (
        <div style={{
          position: 'absolute', top: '20%', width: '100%',
          textAlign: 'center', padding: '0 clamp(20px, 4vw, 60px)',
          opacity: phase >= 4 ? 0 : 1,
          transition: phase >= 4 ? 'opacity 0.75s ease' : 'none',
        }}>
          <div style={{
            fontSize: sz.onceText,
            fontStyle: 'italic', fontWeight: 400,
            color: '#c9a452', letterSpacing: '0.05em', lineHeight: 1.4,
            animation: 'sbFadeUp 0.9s cubic-bezier(0.16,1,0.3,1) both',
          }}>
            Once Upon a Time,
          </div>

          {show(2) && (
            <div style={{
              fontSize: sz.simpleText,
              fontWeight: 800, color: '#ffffff',
              marginTop: 'clamp(8px, 1vw, 14px)',
              letterSpacing: '-0.025em', lineHeight: 1.15,
              animation: 'sbFadeUp 0.75s cubic-bezier(0.16,1,0.3,1) both',
            }}>
              Streaming Was Simple.
            </div>
          )}
        </div>
      )}

      {/* ── PHASE 3: Service badge chaos ── */}
      {show(3) && SERVICES.map((s, i) => {
        const p = POSITIONS[i]
        const alive = phase >= 3 && phase < 5
        return (
          <div key={s.name} style={{
            position: 'absolute', left: p.left, top: p.top,
            background: `${s.color}1e`,
            border: `1.5px solid ${s.color}aa`,
            borderRadius: 8,
            padding: sz.badgePad,
            fontSize: sz.badgeText,
            fontWeight: 700, color: s.color,
            whiteSpace: 'nowrap',
            transform: `rotate(${p.rotate}deg)`,
            opacity: alive ? 1 : 0,
            transition: 'opacity 0.7s ease',
            animation: `sbBadgeIn 0.55s cubic-bezier(0.22,0.68,0,1.2) ${i * 85}ms both,
                        sbBadgeJitter 2s ease-in-out ${i * 85 + 600}ms infinite`,
            pointerEvents: 'none',
          }}>
            {s.name}
          </div>
        )
      })}

      {/* ── PHASE 4: Frustration copy (over the chaos) ── */}
      {show(4) && !show(5) && (
        <div style={{
          position: 'absolute',
          bottom: '28%', width: '100%',
          textAlign: 'center',
          padding: '0 clamp(24px, 5vw, 80px)',
          pointerEvents: 'none',
          animation: 'sbFadeUp 0.7s cubic-bezier(0.16,1,0.3,1) both',
        }}>
          <div style={{
            fontSize: sz.frustText,
            fontWeight: 700,
            color: 'rgba(255,120,120,0.82)',
            letterSpacing: '-0.01em',
            lineHeight: 1.5,
          }}>
            8 subscriptions. Still nothing to watch.
          </div>
          <div style={{
            fontSize: 'clamp(10px, 1vw, 13px)',
            color: 'rgba(255,120,120,0.45)',
            marginTop: 6,
            letterSpacing: '0.02em',
          }}>
            There's a better way.
          </div>
        </div>
      )}

      {/* ── PHASE 5+: Flow hero ── */}
      {show(5) && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          animation: 'sbIconRise 0.85s cubic-bezier(0.16,1,0.3,1) both',
          position: 'relative', zIndex: 2,
        }}>
          <div style={{
            position: 'absolute',
            width: sz.iconGlow, height: sz.iconGlow,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(80,160,235,0.24) 0%, transparent 68%)',
            animation: 'sbGlowPulse 3s ease-in-out infinite',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -64%)', pointerEvents: 'none',
          }} />

          <img
            src={flowIcon}
            alt="Flow"
            style={{
              width: sz.icon, height: sz.icon,
              borderRadius: sz.iconRadius,
              boxShadow: '0 14px 64px rgba(60,140,220,0.55), 0 0 0 1.5px rgba(80,160,240,0.22)',
              marginBottom: 'clamp(12px, 1.4vw, 20px)',
              position: 'relative',
              animation: 'sbFloat 3.5s ease-in-out 0.85s infinite',
            }}
          />

          <div style={{
            fontSize: sz.flowTitle,
            fontWeight: 900,
            background: 'linear-gradient(135deg, #eef6ff 0%, #a5d8ee 48%, #5db0d4 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.03em', lineHeight: 1,
            marginBottom: show(6) ? 'clamp(10px, 1.2vw, 18px)' : 0,
            fontFamily: "'Sora','SF Pro Display','Helvetica Neue',sans-serif",
          }}>
            Flow
          </div>

          {show(6) && (
            <div style={{
              textAlign: 'center',
              maxWidth: 'clamp(240px, 28vw, 380px)',
              marginBottom: 'clamp(20px, 2.4vw, 36px)',
              animation: 'sbFadeUp 0.65s ease both',
            }}>
              <div style={{
                fontSize: sz.tagline,
                fontWeight: 600,
                color: 'rgba(185,225,248,0.9)', lineHeight: 1.6,
              }}>
                One place for all your streams.
              </div>
              <div style={{
                fontSize: sz.sub,
                fontWeight: 400,
                color: 'rgba(140,195,225,0.55)',
                marginTop: 'clamp(5px, 0.6vw, 9px)', lineHeight: 1.65,
              }}>
                Stop searching. Tell Flow what to watch,<br />
                and get straight to Play.
              </div>
            </div>
          )}

          {show(6) && (
            <button
              onClick={(e) => { e.stopPropagation(); exit(false) }}
              style={{
                background: 'linear-gradient(135deg, #16a34a, #15803d)',
                border: 'none', borderRadius: 50,
                padding: sz.ctaPad,
                fontSize: sz.ctaText,
                fontWeight: 700, color: '#fff',
                cursor: 'pointer', letterSpacing: '0.02em',
                boxShadow: '0 8px 36px rgba(22,163,74,0.48)',
                animation: 'sbFadeUp 0.55s ease both',
                fontFamily: "'Sora','SF Pro Display','Helvetica Neue',sans-serif",
              }}
            >
              Enter the Flow →
            </button>
          )}
        </div>
      )}

      {/* Skip hint — visible from phase 1, more prominent after phase 2 */}
      {show(1) && !show(6) && (
        <div style={{
          position: 'absolute', bottom: 'clamp(20px, 3vh, 36px)',
          fontSize: sz.skipText,
          color: show(3) ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.18)',
          letterSpacing: '0.14em', textTransform: 'uppercase',
          animation: 'sbFadeUp 0.6s ease 1.8s both',
          transition: 'color 0.4s ease',
        }}>
          tap anywhere to skip
        </div>
      )}
    </div>
  )
}
